import { inject, Injectable } from '@angular/core';
import { tuiZoneOptimized } from '@taiga-ui/cdk/observables';
import { TUI_WINDOW_SIZE } from '@taiga-ui/cdk/tokens';
import type { TuiMedia } from '@taiga-ui/core/tokens';
import { TUI_MEDIA } from '@taiga-ui/core/tokens';
import { distinctUntilChanged, map, Observable, shareReplay } from 'rxjs';

export type TuiBreakpointMediaKey =
  | keyof Omit<TCustomTuiMedia, 'tablet'>
  | 'desktopMedium'
  | 'mobileLarge';
type TCustomTuiMedia = TuiMedia & {
  desktopMedium: number;
  mobileLarge: number;
};

@Injectable({
  providedIn: 'root',
})
export class CustomBreakpointService extends Observable<TuiBreakpointMediaKey | null> {
  private readonly media: TCustomTuiMedia = {
    ...inject(TUI_MEDIA),
    desktopMedium: 1200,
    tablet: 768,
    mobileLarge: 576,
  };

  private readonly sorted: number[] = Object.values(this.media).sort((a, b) => a - b);
  private readonly invert: Record<number, TuiBreakpointMediaKey> = Object.keys(this.media).reduce(
    (ret, key) => ({
      ...ret,
      [this.media[key as TuiBreakpointMediaKey]]: key,
    }),
    {},
  );

  private readonly stream$: Observable<TuiBreakpointMediaKey> = inject(TUI_WINDOW_SIZE).pipe(
    map(({ width }) => this.sorted.find(size => size > width)),
    map(key => this.invert[key || this.sorted[this.sorted.length - 1] || 0]),
    distinctUntilChanged(),
    tuiZoneOptimized(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor() {
    super(subscriber => this.stream$.subscribe(subscriber));
  }
}
